import { Model } from './model';
import { TransformStringToDayJS, TransformStringToNumber } from './utils';
import dayjs from 'dayjs';
import type { AgentMetrics, AgentParsingErrorCode } from './types';

export enum BankTransactionType {
  DEPOSIT = 'deposit',
  WITHDRAWAL = 'withdrawal',
}

export enum PaymentApplicationStatus {
  NON_PAYMENT = 'non_payment',
  POTENTIAL_PAYMENT = 'potential_payment',
  DRAFT_PAYMENT = 'draft_payment',
  POSTED_PAYMENT = 'posted_payment',
  HANDLED_OUTSIDE = 'handled_outside',
}

export enum AllocationStatus {
  UNALLOCATED = 'unallocated',
  PARTIALLY_ALLOCATED = 'partially_allocated',
  FULLY_ALLOCATED = 'fully_allocated',
}

export class CounterpartyInfo {
  display: string;

  id: string | null;

  partner_count: number;
}

export class BankTransactionBase extends Model {
  id: string;

  account_id: string;

  account_mask: string;

  agent_extractions: {
    counterparty_name: string | null;
    counterparty_raw: string | null;
    invoice_numbers: string[];
  };

  agent_metrics: {
    confidence_reason: string | null;
    confidence_score: number | null;
    error_code: AgentParsingErrorCode | null;
    uncertainties: string[];
  } | null;

  @TransformStringToNumber()
  amount: number;

  @TransformStringToNumber()
  amount_handled_outside: number;

  handled_outside_reason: string | null;

  counterparty_info: CounterpartyInfo | null;

  currency: string | null;

  description: string | null;

  confidence: {
    score: number | null;
    should_post: boolean;
    summary: string | null;
  } | null;

  has_remittance: boolean;

  has_on_account: boolean;

  has_fx_payments: boolean;

  is_payment: boolean | null;

  has_posting_errors: boolean;

  is_agent_processing: boolean;

  non_payment_reason: string | null;

  payout: { id: string; remote_id: string; url: string | null } | null;

  payment_status: PaymentApplicationStatus;

  posted: boolean;

  reference_number: string;

  @TransformStringToDayJS()
  timestamp: dayjs.Dayjs;

  type: BankTransactionType;

  @TransformStringToNumber()
  total_allocated: number;

  get agentMetrics(): AgentMetrics | null {
    return this.agent_metrics
      ? {
          agentConfidenceReason: this.agent_metrics.confidence_reason,
          agentConfidenceScore: this.agent_metrics.confidence_score,
          agentErrorCode: this.agent_metrics.error_code,
          agentUncertainties: this.agent_metrics.uncertainties,
        }
      : null;
  }

  get totalUnallocated(): number {
    const raw = this.amount - this.total_allocated - (this.amount_handled_outside ?? 0);
    return parseFloat(raw.toFixed(2));
  }
}
